// Microsoft Entra ID (MSAL) configuration for the React front end.
//
// The app signs the user in against our M365 tenant, then acquires an access
// token for the ShelterStone backend API and attaches it to every request
// (see authedAxios.js). Access is governed by membership of the Entra security
// group "Shelter Stone web app users"; everything finer-grained comes from the
// app's own permissions model (/me).
//
// All values come from build-time env vars (REACT_APP_*). Client/tenant IDs
// are NOT secrets -- they are visible in any SPA -- but are kept in env vars
// so the same build can target different tenants/registrations.
import { PublicClientApplication } from "@azure/msal-browser";

const TENANT_ID = process.env.REACT_APP_AAD_TENANT_ID;
const CLIENT_ID = process.env.REACT_APP_AAD_CLIENT_ID;

// The API scope exposed by the backend app registration
// ("api://<client-id>/access_as_user"). Defaults to that shape if only the
// client id is provided.
export const API_SCOPE =
  process.env.REACT_APP_AAD_API_SCOPE ||
  (CLIENT_ID ? `api://${CLIENT_ID}/access_as_user` : "");

// Set REACT_APP_AUTH_ENABLED=false to run locally without Entra configured.
export const AUTH_ENABLED =
  (process.env.REACT_APP_AUTH_ENABLED || "true").trim().toLowerCase() !== "false";

// Object ID of the security group required to use the app ("Shelter Stone web
// app users"). Deliberately has no default — the id is not committed to the repo
// (locally it comes from frontend/.env.local, in CI from a repo secret).
//
// When it is unset the client-side check is skipped and the user is let through
// to the app; the backend still enforces membership, so an outsider just gets
// 403s instead of the tidy "access denied" screen. This check is only ever UX.
export const REQUIRED_GROUP_ID = (
  process.env.REACT_APP_AAD_REQUIRED_GROUP_ID || ""
)
  .trim()
  .toLowerCase();

export const msalConfig = {
  auth: {
    clientId: CLIENT_ID,
    authority: `https://login.microsoftonline.com/${TENANT_ID}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  cache: {
    // localStorage, not sessionStorage, because **sessionStorage is per tab**: a
    // link opened in a new tab started with an empty MSAL cache, so `AuthGate`
    // found no account and showed the branded sign-in page to someone already
    // signed in. Pressing the button then bounced off Entra's still-valid session
    // cookie and straight back in — several redirects to arrive where they were
    // going. The incident dialog's patient-history links are all new tabs, so this
    // was on the path of the app's most time-critical journey.
    //
    // Two consequences to know. The cache is shared across tabs of this origin, so
    // signing in once covers all of them; and it outlives the browser being closed,
    // so someone stays signed in until the refresh token expires rather than being
    // signed out with the last tab. Sign out is the way off a shared device. The
    // group gate and every backend permission still apply either way — this changes
    // where the token is kept, not what it grants.
    cacheLocation: "localStorage",
    storeAuthStateInCookie: false,
  },
};

// Scopes requested at login / token acquisition for calling the backend.
export const loginRequest = {
  scopes: API_SCOPE ? [API_SCOPE] : ["openid", "profile"],
};

export const msalInstance = new PublicClientApplication(msalConfig);
